import React, { FC, useEffect } from 'react';

import { ContactRepModalDetails } from '@mb/lib';
import { AnalyticsEventLocation } from '@mb/lib';
import { FormikProps } from 'formik';
import { CompleteState } from './CompleteState';
import * as Styled from './ContactRepModalView.styles';
import { EmptyState } from './EmptyState';
import { ContactRepFormState, getDefaultInitialValues } from './Form';
import { FormComponent } from './FormComponent';
import { LoadingSkeleton } from './LoadingSkeleton';
import { MessageData, sendMessage } from '../../api/messages';
import {
  useRepresentatives,
  useModalPlacement,
  useAnalyticsEvents,
} from '../../hooks';
import { Option } from '../../types';
import { getContactRepMessageTemplate } from '../../utils/getContactRepMessageTemplate';
import {
  AnalyticsEventAPQAction,
  AnalyticsEventContactRepType,
  AnalyticsEventName,
  sendAnalyticsEvent,
} from '../../utils/helpers/analytics';

interface ContactRepModalProps {
  isOpen?: boolean;
  setIsOpen: (isOpen: boolean) => void;
  modalDetails?: ContactRepModalDetails;
}

export const ContactRepModalView: FC<ContactRepModalProps> = (props) => {
  const formRef = React.useRef<FormikProps<ContactRepFormState>>(null);
  const { isOpen = false, setIsOpen, modalDetails } = props;
  const [isFormSent, setIsFormSent] = React.useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const [isFormSentInProgress, setIsFormSentInProgress] = React.useState(false);
  const [selectedRepresentative, setSelectedRepresentative] =
    React.useState<Option | null>(null);
  const [isError, setIsError] = React.useState(false);
  const { modalPlacement, modalXOffset } = useModalPlacement(modalDetails);
  const {
    representativesList,
    isLoading,
    isEmpty,
    isError: isRepresentativesError,
  } = useRepresentatives();
  useAnalyticsEvents(isOpen, modalDetails);

  useEffect(() => {
    if (isRepresentativesError) {
      setIsError(true);
    }
  }, [isRepresentativesError]);

  useEffect(() => {
    if (!isFormSent) {
      return;
    }
    const timeoutId = setTimeout(() => {
      setIsOpen(false);
      setIsFormSent(false);
      formRef.current?.resetForm({
        values: getDefaultInitialValues({
          defaultSelectedRepresentative: representativesList[0],
        }),
      });
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [isFormSent]);

  const handleCancel = () => {
    setIsOpen(false);
    formRef.current?.resetForm({
      values: getDefaultInitialValues({
        defaultSelectedRepresentative: representativesList[0],
      }),
    });
  };

  const handleSendMessage = () => {
    setIsFormSentInProgress(true);
    formRef.current?.submitForm();
  };

  const onSubmitForm = async (values: ContactRepFormState) => {
    if (!modalDetails) {
      return;
    }
    const {
      project_id,
      product_id,
      manufacturer_sku,
      manufacturer,
      product_name,
      event_location,
    } = modalDetails.data;

    const keyToValueMap = {
      pricingRequest: AnalyticsEventContactRepType.PRICING,
      productLeadTime: AnalyticsEventContactRepType.LEAD_TIME,
      requestMeeting: AnalyticsEventContactRepType.REQUEST_MEETING,
      installationImagery: AnalyticsEventContactRepType.INSTALLATION_IMAGES,
      other: AnalyticsEventContactRepType.OTHER,
      message: AnalyticsEventContactRepType.PERSONAL_MESSAGE,
    };

    const types = Object.keys(keyToValueMap)
      .filter(
        (key) =>
          // @ts-expect-error // TODO: fix types
          values[key] === true || (key === 'message' && values[key]?.length),
      )
      // @ts-expect-error // TODO: fix types
      .map((key) => keyToValueMap[key]);

    const sortedTypes = types.sort().join(', ');
    const data: MessageData = {
      product_id,
      project_id,
      manufacturer_sku,
      manufacturer,
      rep_id: values.representative.value.toString(),
      message: values.message,
      message_source: 'contact_rep_modal',
      quick_topics: getContactRepMessageTemplate(values),
      product_name,
    };
    sendAnalyticsEvent({
      // TODO: refactor without type assertion, or add comment
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      event_location: event_location as AnalyticsEventLocation,
      event: AnalyticsEventName.CONTACT_REP,
      action: AnalyticsEventAPQAction.SEND_MESSAGE,
      type: sortedTypes,
    });
    setSelectedRepresentative(values.representative);
    return await sendMessage(data)
      .then(() => {
        setIsFormSentInProgress(false);
        setIsFormSent(true);
      })
      .catch(() => {
        setIsError(true);
        setIsFormSentInProgress(false);
      });
  };

  if (!modalDetails) {
    return null;
  }

  return (
    <Styled.ContactRepModal
      isOpen={isOpen}
      modalPlacement={modalPlacement}
      ref={modalRef}
      isFormSent={isFormSent}
      modalXOffset={modalXOffset}
      isEmpty={isEmpty}
      isError={isError}
    >
      {isLoading ? (
        <LoadingSkeleton />
      ) : representativesList.length > 0 && !isError ? (
        !isFormSent ? (
          <FormComponent
            formRef={formRef}
            representativesList={representativesList}
            isFormSentInProgress={isFormSentInProgress}
            handleCancel={handleCancel}
            handleSendMessage={handleSendMessage}
            onSubmitForm={onSubmitForm}
          />
        ) : (
          <CompleteState selectedRepresentative={selectedRepresentative} />
        )
      ) : (
        <EmptyState onButtonClick={handleCancel} isError={isError} />
      )}
    </Styled.ContactRepModal>
  );
};
